// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { OrderApprovalsDataViewContext } from "@insite/client-framework/Store/Data/OrderApprovals/OrderApprovalsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { PaginationStyle } from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { xScreen } from "@rc/XDL/Utilities";
import React, { useContext } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps;

export interface orderApprovalListOrderNumberAndPaginationContainerStyles {
    topPaginationItemGrid?: GridItemProps;
    quoteCountText?: TypographyPresentationProps;
}

export const headerStyles: orderApprovalListOrderNumberAndPaginationContainerStyles = {
    topPaginationItemGrid: {
        width: [7, 6, 6, 6, 4],
        css: css`
            &&&& {
                ${PaginationStyle} {
                    margin: 0;
                }
                ${xScreen("lte", "xs")} {
                    [class^="PerPageSelect"] {
                        display: none;
                    }
                }
            }
        `,
    },
    quoteCountText: {
        css: css`
            font-size: 18px;
            font-weight: 500;
            line-height: 24px;
            color: ${getColor("common.ui7")};
        `,
    },
};
const styles = headerStyles;

const OrderApprovalListOrderNumberAndPaginationContainer = ({ id }: Props) => {
    const orderApprovalsDataView = useContext(OrderApprovalsDataViewContext);
    const orderApprovalsCount = orderApprovalsDataView.value
        ? orderApprovalsDataView.pagination
            ? orderApprovalsDataView.pagination.totalItemCount
            : 0
        : undefined;

    if (orderApprovalsCount === 0) {
        return null;
    }

    return (
        <StyledWrapper
            css={css`
                padding: 13px;
                margin: 32px 0 25px;
                border: 1px solid ${getColor("common.border")};
            `}
        >
            <GridContainer>
                <GridItem
                    width={[5, 6, 6, 6, 8]}
                    css={css`
                        > div,
                        span {
                            margin: auto 0;
                        }
                    `}
                >
                    <Typography {...styles.quoteCountText} data-test-selector="orderApprovalList_resultCount">
                        {orderApprovalsCount} {translate("Orders")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.topPaginationItemGrid}>
                    <Zone contentId={id} zoneName="TopPaginaton" />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderApprovalListOrderNumberAndPaginationContainer,
    definition: {
        group: "Order Approval List",
        displayName: "Order Number And Pagination Container",
        allowedContexts: ["OrderApprovalListPage"],
    },
};

export default widgetModule;
